import { supabase } from '../lib/supabase';

class MemoService {
  async getMemosByUser(userId) {
    try {
      const { data, error } = await supabase
        .from('video_memos')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // 動画情報を別途取得
      if (data && data.length > 0) {
        const youtubeIds = data.map(memo => memo.youtube_video_id).filter(Boolean);
        if (youtubeIds.length > 0) {
          const { data: videos } = await supabase
            .from('videos')
            .select('*')
            .in('youtube_video_id', youtubeIds);

          const videosMap = new Map(
            (videos || []).map(video => [video.youtube_video_id, video])
          );

          const memosWithVideos = data.map(memo => ({
            ...memo,
            video: videosMap.get(memo.youtube_video_id) || null
          }));

          return { data: memosWithVideos, error: null };
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user memos:', error);
      return { data: null, error };
    }
  }

  async getMemoByVideoId(userId, youtubeVideoId) {
    try {
      const { data, error } = await supabase
        .from('video_memos')
        .select('*')
        .eq('user_id', userId)
        .eq('youtube_video_id', youtubeVideoId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching memo by video:', error);
      return { data: null, error };
    }
  }

  async createOrUpdateMemo(userId, youtubeVideoId, memo) {
    try {
      // まず既存のメモがあるか確認
      const { data: existingMemo } = await supabase
        .from('video_memos')
        .select('id')
        .eq('user_id', userId)
        .eq('youtube_video_id', youtubeVideoId)
        .single();

      let result;
      if (existingMemo) {
        // 更新
        result = await supabase
          .from('video_memos')
          .update({
            memo: memo,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('youtube_video_id', youtubeVideoId);
      } else {
        // 新規作成
        result = await supabase
          .from('video_memos')
          .insert({
            user_id: userId,
            youtube_video_id: youtubeVideoId,
            memo: memo,
            updated_at: new Date().toISOString()
          });
      }

      if (result.error) throw result.error;
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Error saving memo:', error);
      return { data: null, error };
    }
  }

  async deleteMemo(userId, youtubeVideoId) {
    try {
      const { error } = await supabase
        .from('video_memos')
        .delete()
        .eq('user_id', userId)
        .eq('youtube_video_id', youtubeVideoId);

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting memo:', error);
      return { success: false, error };
    }
  }

  async getVideosWithMemos(userId) {
    try {
      // まずシンプルなクエリでvideosテーブルからデータ取得
      const videosResponse = await supabase
        .from('videos')
        .select('*');

      if (videosResponse.error) {
        console.error('Videos query error:', videosResponse.error);
        throw videosResponse.error;
      }

      console.log('Videos from database:', videosResponse.data);

      // youtube_video_idがあるものだけフィルタリング
      const videosWithYoutubeId = (videosResponse.data || []).filter(
        video => video.youtube_video_id != null
      );

      console.log('Videos with YouTube ID:', videosWithYoutubeId);

      // video_memosテーブルからユーザーのメモとカテゴリを取得
      const memosResponse = await supabase
        .from('video_memos')
        .select('youtube_video_id, memo, category, updated_at')
        .eq('user_id', userId);

      console.log('User memos:', memosResponse.data);

      // メモのエラーは無視（メモがなくてもOK）
      if (memosResponse.error) {
        console.error('Memos query error (ignored):', memosResponse.error);
      }

      const memosMap = new Map(
        (memosResponse.data || []).map(memo => [
          memo.youtube_video_id,
          { 
            memo: memo.memo, 
            category: memo.category,
            updated_at: memo.updated_at 
          }
        ])
      );

      const videosWithMemos = videosWithYoutubeId.map(video => {
        const memoData = memosMap.get(video.youtube_video_id);
        return {
          ...video,
          memo: memoData?.memo || '',
          // ユーザーが保存したカテゴリを優先、なければ動画のデフォルトカテゴリ
          category: memoData?.category || video.category || '未分類',
          memo_updated_at: memoData?.updated_at || null
        };
      });

      return { data: videosWithMemos, error: null };
    } catch (error) {
      console.error('Error fetching videos with memos:', error);
      return { data: null, error };
    }
  }

  subscribeToMemoChanges(userId, callback) {
    const subscription = supabase
      .channel(`video_memos:user_id=eq.${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'video_memos',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )
      .subscribe();

    return subscription;
  }

  unsubscribeFromMemoChanges(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }

  async searchMemos(userId, searchTerm) {
    try {
      const { data, error } = await supabase
        .from('video_memos')
        .select('*')
        .eq('user_id', userId)
        .ilike('memo', `%${searchTerm}%`)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // 動画情報を別途取得
      if (data && data.length > 0) {
        const youtubeIds = data.map(memo => memo.youtube_video_id).filter(Boolean);
        if (youtubeIds.length > 0) {
          const { data: videos } = await supabase
            .from('videos')
            .select('*')
            .in('youtube_video_id', youtubeIds);

          const videosMap = new Map(
            (videos || []).map(video => [video.youtube_video_id, video])
          );

          const memosWithVideos = data.map(memo => ({
            ...memo,
            video: videosMap.get(memo.youtube_video_id) || null
          }));

          return { data: memosWithVideos, error: null };
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error searching memos:', error);
      return { data: null, error };
    }
  }
}

export default new MemoService();